import React, { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { usersApi } from '../../services/api'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`

const Card = styled.div`
    background: white;
    border-radius: 16px;
    padding: 40px;
    max-width: 500px;
    width: 100%;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`

const Title = styled.h1`
    color: #333;
    font-size: 24px;
    margin-bottom: 8px;
    text-align: center;
`

const Subtitle = styled.p`
    color: #666;
    font-size: 14px;
    margin-bottom: 32px;
    text-align: center;
`

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 24px;
`

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const Label = styled.label`
    font-weight: 600;
    color: #333;
    font-size: 14px;
`

const Input = styled.input`
    padding: 12px 16px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 16px;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #667eea;
    }
`

const Select = styled.select`
    padding: 12px 16px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 16px;
    background: white;
    cursor: pointer;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #667eea;
    }
`

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
    padding: 14px 24px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;

    ${props => props.$variant === 'secondary' ? `
        background: #f0f0f0;
        color: #333;

        &:hover {
            background: #e0e0e0;
        }
    ` : `
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }
    `}

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    margin-top: 8px;
`

const InfoBox = styled.div`
    background: #f0f4ff;
    border: 1px solid #667eea;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 24px;

    h3 {
        color: #667eea;
        margin: 0 0 8px 0;
        font-size: 14px;
    }

    p {
        color: #555;
        margin: 0;
        font-size: 13px;
        line-height: 1.5;
    }
`

const ErrorMessage = styled.div`
    background: #fee;
    border: 1px solid #f88;
    border-radius: 8px;
    padding: 12px;
    color: #c00;
    font-size: 14px;
`

const SuccessMessage = styled.div`
    background: #efe;
    border: 1px solid #8c8;
    border-radius: 8px;
    padding: 12px;
    color: #080;
    font-size: 14px;
`

const PracticasSetup: React.FC = () => {
    const navigate = useNavigate()
    const { user, refreshPracticasInfo, practicasInfo } = useAuth()

    const [formData, setFormData] = useState({
        total_horas: practicasInfo?.total_horas?.toString() || '',
        horario: practicasInfo?.horario || '',
        tipo_practicas: practicasInfo?.tipo_practicas || ''
    })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
        setError('')
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setSuccess('')

        // Validation
        if (!formData.total_horas || !formData.horario || !formData.tipo_practicas) {
            setError('Por favor, completa todos los campos.')
            return
        }

        const totalHoras = parseInt(formData.total_horas)
        if (isNaN(totalHoras) || totalHoras < 1 || totalHoras > 2000) {
            setError('Las horas totales deben estar entre 1 y 2000.')
            return
        }

        setIsSubmitting(true)

        try {
            await usersApi.updatePracticasInfo(user!.id, {
                total_horas: totalHoras,
                horario: formData.horario as 'media_jornada' | 'jornada_completa',
                tipo_practicas: formData.tipo_practicas as 'curriculares' | 'extracurriculares'
            })

            setSuccess('¡Información guardada correctamente!')
            await refreshPracticasInfo()

            // Navigate to home after a short delay
            setTimeout(() => {
                navigate('/')
            }, 1500)
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Error al guardar la información.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Container>
            <Card>
                <Title>👋 ¡Bienvenido/a, {user?.display_name}!</Title>
                <Subtitle>
                    Antes de continuar, necesitamos algunos datos sobre tus prácticas.
                </Subtitle>

                <InfoBox>
                    <h3>ℹ️ ¿Por qué necesitamos esta información?</h3>
                    <p>
                        Esta información nos ayuda a gestionar mejor las asignaciones del equipo
                        y a incluirte en proyectos que se ajusten a tu disponibilidad.
                    </p>
                </InfoBox>

                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}

                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label htmlFor="total_horas">Total de horas de prácticas *</Label>
                        <Input
                            type="number"
                            id="total_horas"
                            name="total_horas"
                            placeholder="Ej: 300"
                            min="1"
                            max="2000"
                            value={formData.total_horas}
                            onChange={handleChange}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="horario">Horario *</Label>
                        <Select
                            id="horario"
                            name="horario"
                            value={formData.horario}
                            onChange={handleChange}
                        >
                            <option value="">Selecciona tu horario</option>
                            <option value="jornada_completa">Jornada completa (8h/día)</option>
                            <option value="media_jornada">Media jornada (4h/día)</option>
                        </Select>
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="tipo_practicas">Tipo de prácticas *</Label>
                        <Select
                            id="tipo_practicas"
                            name="tipo_practicas"
                            value={formData.tipo_practicas}
                            onChange={handleChange}
                        >
                            <option value="">Selecciona el tipo</option>
                            <option value="curriculares">Curriculares</option>
                            <option value="extracurriculares">Extracurriculares</option>
                        </Select>
                    </FormGroup>

                    <ButtonGroup>
                        <Button type="submit" disabled={isSubmitting} style={{ width: '100%' }}>
                            {isSubmitting ? 'Guardando...' : 'Guardar y continuar'}
                        </Button>
                    </ButtonGroup>
                </Form>
            </Card>
        </Container>
    )
}

export default PracticasSetup
